import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { PushService } from 'rev-shared/push/PushService';
import { UserContextService } from 'rev-shared/security/UserContext.Service';

export interface ISparkRoom {
	created: string;
	id: string;
	lastActivity: string;
	title: string;
}

@Injectable({
	providedIn: 'root'
})
export class SparkService {
	private _categorySubscriptions: any;

	constructor(
		private http: HttpClient,
		private PushBus: PushBus,
		private PushService: PushService,
		private UserContext: UserContextService
	) { }

	public get categorySubscriptions(): any {
		return this._categorySubscriptions;
	}

	public cancelCategorySubscription(categoryId: string): Promise<any> {
		return this.PushService.dispatchCommand('media:CancelSparkCategorySubscription', {
			categoryId,
			userId: this.UserContext.getUser().id
		});
	}

	public createCategorySubscription(room: any, categoryId: string): Promise<any> {
		return this.PushService.dispatchCommand('media:CreateSparkCategorySubscription', {
			categoryId,
			roomId: room.id,
			roomTitle: room.title,
			userId: this.UserContext.getUser().id
		});
	}

	public getRooms(isGroupsOnly: boolean = false): Promise<ISparkRoom[]> {
		const userId = this.UserContext.getUser().id;
		return lastValueFrom(this.http.get<any>(`/network/users/${userId}/spark/rooms`, { params: { isGroupsOnly: isGroupsOnly + '' } }))
			.then(response => response.rooms)
			.catch(err =>
				Promise.reject(err.status === 404 ?
					{ authenticationRequired: true } :
					err
				)
			);
	}

	public getWebexRoomDetails(roomId: string): Promise<any> {
		const userId = this.UserContext.getUser().id;

		return lastValueFrom(this.http.get(`/network/users/${userId}/spark/rooms/${roomId}/details`));
	}

	public loadCategorySubscriptions(): Promise<any> {
		this._categorySubscriptions = {};

		if (!this.UserContext.isUserAuthenticated()) {
			return Promise.resolve();
		}

		const userId = this.UserContext.getUser().id;
		return lastValueFrom(this.http.get<any>(`/media/users/${userId}/spark-subscriptions`))
			.then(response =>
				response.categorySubscriptions.forEach(subscription => {
					this._categorySubscriptions[subscription.categoryId] = subscription;
				})
			);
	}

	public monitorCategorySubscriptions(): IUnsubscribe {
		const userId: string = this.UserContext.getUser().id;

		return this.PushBus.subscribe(userId, {
			SparkCategorySubscriptionCreated: data => {
				this._categorySubscriptions[data.categoryId] = {
					roomId: data.roomId,
					roomTitle: data.roomTitle
				};
			},

			SparkCategorySubscriptionCancelled: data => {
				delete this._categorySubscriptions[data.categoryId];
			}
		});
	}

	public onAuthenticate(handler: () => void): IUnsubscribe {
		const userId: string = this.UserContext.getUser().id;

		const unsubscribe = this.PushBus.subscribe(userId, {
			SparkDetailsSaved() {
				unsubscribe();
				handler();
			}
		});

		return unsubscribe;
	}

	public shareVideo(message: any): Promise<any> {
		return lastValueFrom(this.http.post('/network/users/spark/rooms/share-video', message, {}));
	}
}
